/**
 * Captain Scorecard Page - Mobile scorecard entry for team captains
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Target, Save, Send, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { OutingProvider, useOuting } from '../context/OutingContext';
import { useOutingApi } from '../hooks/useOutingApi';
import PinDistanceCalculator from '../components/PinDistanceCalculator';
import { PencilWhipPopup } from '../components/LiveNotifications';
import { 
  GAME_FORMATS, getScoreStyleClass, getRandomPencilWhipMessage 
} from '../utils/outingHelpers';
import { 
  getScoreType, getNotificationTypeForScore, buildScorecardSummary 
} from '../utils/scoringCalculations';

// Default par array
const DEFAULT_PARS = [4, 3, 4, 5, 4, 3, 4, 4, 5, 4, 3, 4, 5, 4, 3, 4, 4, 5];

function CaptainScorecardContent({ user }) {
  const { outingId, teamId } = useParams();
  const navigate = useNavigate();
  const api = useOutingApi();
  const { currentOuting, fetchOuting } = useOuting();
  
  const [team, setTeam] = useState(null);
  const [holes, setHoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCTPCalculator, setShowCTPCalculator] = useState(false);
  const [ctpHole, setCTPHole] = useState(null);
  const [showPencilWhip, setShowPencilWhip] = useState(false);
  const [pendingScore, setPendingScore] = useState(null);

  useEffect(() => {
    fetchOuting(outingId);
    loadTeam();
  }, [outingId, teamId]);

  useEffect(() => {
    if (currentOuting) {
      // Initialize holes with pars from course data and CTP designation
      const ctpHoles = currentOuting.game_settings?.ctp?.holes || [];
      const coursePars = currentOuting.hole_pars || DEFAULT_PARS;
      
      const initialHoles = coursePars.map((par, i) => ({
        hole: i + 1,
        par: par || 4,
        score: null,
        is_ctp_hole: ctpHoles.includes(i + 1),
        ctp_distance: null
      }));
      setHoles(initialHoles);
    }
  }, [currentOuting]);

  const loadTeam = async () => {
    try {
      const result = await api.listTeams(outingId);
      if (result.success) {
        const foundTeam = result.teams.find(t => t.id === teamId);
        setTeam(foundTeam);
      }
    } catch (err) {
      console.error('Failed to load team:', err);
    }
  };

  const handleScoreChange = (holeIndex, value) => {
    const score = value === '' ? null : parseInt(value, 10);
    if (score !== null && (score < 1 || score > 15)) return;
    
    const par = holes[holeIndex].par;
    const scoreType = getScoreType(score, par);
    
    // Check if this is a great score (birdie or better)
    if (score && ['birdie', 'eagle', 'albatross'].includes(scoreType)) {
      setPendingScore({ holeIndex, score });
      setShowPencilWhip(true);
      return;
    }
    
    updateScore(holeIndex, score);
  };

  const updateScore = (holeIndex, score) => {
    setHoles(prev => prev.map((h, i) => 
      i === holeIndex ? { ...h, score } : h
    ));
    
    // Broadcast notification if good score
    const par = holes[holeIndex].par;
    const notificationType = getNotificationTypeForScore(score, par);
    if (notificationType && team) {
      broadcastScore(notificationType, holeIndex + 1);
    }
  };

  const confirmPencilWhipScore = () => {
    if (pendingScore) {
      updateScore(pendingScore.holeIndex, pendingScore.score);
    }
    setShowPencilWhip(false);
    setPendingScore(null);
  };

  const recountScore = () => {
    setShowPencilWhip(false);
    setPendingScore(null);
    toast.info('Take your time and recount those strokes! 😄');
  };

  const broadcastScore = async (type, hole) => {
    try {
      await api.broadcastNotification(outingId, {
        notification_type: type,
        team_id: teamId,
        hole,
        player_name: user?.display_name
      }, user?.user_id);
    } catch (err) {
      console.error('Failed to broadcast:', err);
    }
  };

  const openCTPCalculator = (hole) => {
    setCTPHole(hole);
    setShowCTPCalculator(true);
  };

  const handleCTPSubmit = async (ctpData) => {
    try {
      await api.submitCTPEntry(outingId, ctpData, user?.user_id);
      
      // Update local hole state
      setHoles(prev => prev.map((h, i) => 
        i === ctpHole - 1 ? { ...h, ctp_distance: ctpData.distance_display } : h
      ));
      
      toast.success('CTP entry submitted!');
      setShowCTPCalculator(false);
      setCTPHole(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.submitScorecard(outingId, {
        team_id: teamId,
        holes,
        player_scores: [], // For team formats, we use team score
        submitted_by: user?.display_name || 'Captain'
      }, user?.user_id);
      
      toast.success('Scorecard saved!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all holes have scores
    const missingHoles = holes.filter(h => h.score === null).map(h => h.hole);
    if (missingHoles.length > 0) {
      toast.error(`Missing scores for holes: ${missingHoles.join(', ')}`);
      return;
    }
    
    setSaving(true);
    try {
      await api.submitScorecard(outingId, {
        team_id: teamId,
        holes,
        player_scores: [],
        submitted_by: user?.display_name || 'Captain'
      }, user?.user_id);
      
      toast.success('Scorecard submitted for review!');
      navigate(`/outings/${outingId}/leaderboard`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const summary = buildScorecardSummary(
    holes.map(h => h.score),
    holes.map(h => h.par)
  );

  const format = GAME_FORMATS[currentOuting?.game_settings?.format];

  if (!team || !currentOuting) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="ps-theme-page min-h-screen pb-32" data-testid="captain-scorecard-theme-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 p-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/outings/${outingId}`)}
            className="text-zinc-400"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{team.name}</h1>
            <p className="text-sm text-zinc-400">{currentOuting.name}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black">{summary.total || '—'}</p>
            <p className={`text-sm ${summary.relativeToPar <= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {summary.relativeDisplay}
            </p>
          </div>
        </div>
      </div>

      {/* Game Format Info */}
      <div className="p-4 bg-[#121212] border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{format?.icon || '🏌️'}</span>
          <div>
            <p className="font-semibold text-white">{format?.name || 'Scramble'}</p>
            <p className="text-xs text-zinc-400">{currentOuting.game_settings?.custom_rules || format?.description}</p>
          </div>
        </div>
      </div>

      {/* Scorecard */}
      <div className="p-4 space-y-2">
        {/* Front 9 */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Front 9</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="grid grid-cols-3 gap-2">
              {holes.slice(0, 9).map((hole, i) => (
                <HoleInput
                  key={i}
                  hole={hole}
                  onChange={(v) => handleScoreChange(i, v)}
                  onCTPClick={() => openCTPCalculator(hole.hole)}
                />
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-sm">
              <span className="text-zinc-400">Front 9</span>
              <span className="font-bold">{summary.front9 || '—'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Back 9 */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Back 9</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="grid grid-cols-3 gap-2">
              {holes.slice(9, 18).map((hole, i) => (
                <HoleInput
                  key={i + 9}
                  hole={hole}
                  onChange={(v) => handleScoreChange(i + 9, v)}
                  onCTPClick={() => openCTPCalculator(hole.hole)}
                />
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-sm">
              <span className="text-zinc-400">Back 9</span>
              <span className="font-bold">{summary.back9 || '—'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex justify-between text-sm">
              <span className="text-green-400">🐦 {summary.stats.birdies}</span>
              <span className="text-white">⛳ {summary.stats.pars}</span>
              <span className="text-red-400">📛 {summary.stats.bogeys}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#0A0A0A] border-t border-white/10">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 border-white/20"
          >
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-green-600 hover:bg-green-500"
            data-testid="submit-scorecard-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit
          </Button>
        </div>
      </div>

      {/* CTP Calculator */}
      {showCTPCalculator && (
        <PinDistanceCalculator
          outingId={outingId}
          teamId={teamId}
          hole={ctpHole}
          playerName={user?.display_name}
          onSubmit={handleCTPSubmit}
          onClose={() => {
            setShowCTPCalculator(false);
            setCTPHole(null);
          }}
        />
      )}

      {/* Pencil Whipping Popup */}
      <PencilWhipPopup
        show={showPencilWhip}
        onConfirm={confirmPencilWhipScore}
        onRecount={recountScore}
      />
    </div>
  );
}

function HoleInput({ hole, onChange, onCTPClick }) {
  return (
    <div className={`relative rounded-lg p-2 ${
      hole.is_ctp_hole ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-white/5'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-zinc-400">Hole {hole.hole}</span>
        <span className="text-xs text-zinc-500">Par {hole.par}</span>
      </div>
      
      <Input
        type="number"
        min="1"
        max="15"
        value={hole.score || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`text-center text-lg font-bold bg-transparent border-white/20 h-10 ${
          getScoreStyleClass(hole.score, hole.par)
        }`}
        data-testid={`hole-${hole.hole}-input`}
      />
      
      {hole.is_ctp_hole && (
        <div className="mt-2">
          <Badge 
            className="bg-yellow-500/20 text-yellow-400 text-xs cursor-pointer w-full justify-center"
            onClick={onCTPClick}
          >
            <Target className="w-3 h-3 mr-1" />
            {hole.ctp_distance || 'CTP'}
          </Badge>
        </div>
      )}
    </div>
  );
}

export default function CaptainScorecardPage({ user }) {
  return (
    <OutingProvider>
      <CaptainScorecardContent user={user} />
    </OutingProvider>
  );
}
